import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  AlertCircle,
  Bell,
  Briefcase,
  Calendar,
  Clock,
  CreditCard,
  Edit3,
  FileText,
  Home,
  IndianRupee,
  Loader2,
  LogOut,
  Plus,
  RefreshCw,
  Send,
  Settings,
  Upload,
  User,
  UserCheck,
  UserPlus,
  UserX,
  Users,
  Wallet,
  XCircle
} from 'lucide-react';
import AttendanceManagement from '../admin/AttendanceManagement';
import EmployeeManagement from '../admin/EmployeeManagement';
import LoansAdvancesManagement from '../admin/LoansAdvancesManagement';
import PayrollEditor from '../admin/PayrollEditor';
import LeaveManagement from '../admin/LeaveManagement';
import ShiftRoster from './ShiftRoster';
import Reports from '../admin/Reports';
import { buildApiUrl } from '../../utils/apiSetup';

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const getArray = (value, key) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value[key])) return value[key];
  return [];
};

const normalizePayrollRuns = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.runs)) return value.runs;
  return [];
};

const statusClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['approved', 'active', 'present', 'checker_approved'].includes(normalized)) return 'bg-green-100 text-green-800 border-green-200';
  if (['rejected', 'absent'].includes(normalized)) return 'bg-red-100 text-red-800 border-red-200';
  if (['draft', 'pending'].includes(normalized)) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
};

const extractError = async (response, fallback) => {
  try {
    const data = await response.json();
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail) && data.detail.length > 0) return data.detail[0].msg || fallback;
  } catch {
    return fallback;
  }
  return fallback;
};

const Section = ({ title, subtitle, icon: Icon, action, children }) => (
  <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-blue-50 text-blue-700">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const SummaryCard = ({ title, value, subtitle, icon: Icon, color, actionLabel, onClick, disabled }) => (
  <motion.div whileHover={{ y: -2 }} className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
    {actionLabel && (
      <button
        onClick={onClick}
        disabled={disabled}
        className="mt-4 w-full px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {actionLabel}
      </button>
    )}
  </motion.div>
);

const ActionButton = ({ icon: Icon, label, onClick, tone = 'blue', disabled }) => {
  const tones = {
    blue: 'border-blue-200 text-blue-700 hover:bg-blue-50',
    green: 'border-green-200 text-green-700 hover:bg-green-50',
    orange: 'border-orange-200 text-orange-700 hover:bg-orange-50',
    purple: 'border-purple-200 text-purple-700 hover:bg-purple-50',
    gray: 'border-gray-200 text-gray-700 hover:bg-gray-50'
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-3 border rounded-lg flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${tones[tone] || tones.blue}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
};

const DataList = ({ items, emptyText, renderItem }) => (
  <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
    {items.length === 0 ? (
      <div className="p-6 text-center text-sm text-gray-500">{emptyText}</div>
    ) : (
      items.map(renderItem)
    )}
  </div>
);

const getMonthName = (month) => new Date(2026, Number(month || 1) - 1).toLocaleString('default', { month: 'long' });

const MakerPayrollDraftPanel = ({ onRefreshDashboard }) => {
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs?limit=50'), { headers });
      if (!response.ok) throw new Error(await extractError(response, 'Unable to load payroll runs.'));
      setPayrollRuns(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const approvedRuns = payrollRuns
    .filter(run => run.status === 'approved')
    .sort((a, b) => (b.payroll_year * 12 + b.payroll_month) - (a.payroll_year * 12 + a.payroll_month));

  const draftRuns = payrollRuns.filter(run => run.status === 'draft');

  const nextPeriod = (() => {
    if (approvedRuns.length === 0) {
      const now = new Date();
      return { month: now.getMonth() + 1, year: now.getFullYear() };
    }
    const latest = approvedRuns[0];
    const nextMonth = latest.payroll_month === 12 ? 1 : latest.payroll_month + 1;
    const nextYear = latest.payroll_month === 12 ? latest.payroll_year + 1 : latest.payroll_year;
    return { month: nextMonth, year: nextYear };
  })();

  const createDraft = async () => {
    setCreating(true);
    setError('');
    setSuccess('');
    const { month, year } = nextPeriod;
    const cutoffDate = `${year}-${String(month).padStart(2, '0')}-25`;
    const fromDate = month === 1 ? `${year - 1}-12-26` : `${year}-${String(month - 1).padStart(2, '0')}-26`;
    const toDate = cutoffDate;

    try {
      const response = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          payroll_month: month,
          payroll_year: year,
          run_type: 'fulltime',
          description: 'Generated from maker dashboard',
          cutoff_date: cutoffDate,
          attendance_from_date: fromDate,
          attendance_to_date: toDate
        })
      });
      if (!response.ok) throw new Error(await extractError(response, 'Unable to create payroll draft.'));
      setSuccess(`Payroll draft created for ${getMonthName(month)} ${year}.`);
      await loadRuns();
      onRefreshDashboard?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {(error || success) && (
        <div className={`p-4 rounded-lg border ${error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
          {error || success}
        </div>
      )}

      <Section
        title="Payroll Draft Processing"
        subtitle="Maker can generate and edit payroll calculations. Approval, release, and finalization are intentionally not available here."
        icon={IndianRupee}
        action={<ActionButton icon={RefreshCw} label="Refresh" tone="gray" onClick={loadRuns} />}
      >
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-blue-700">Next payroll draft</p>
            <h3 className="text-xl font-semibold text-blue-950">{getMonthName(nextPeriod.month)} {nextPeriod.year}</h3>
            <p className="text-sm text-blue-700 mt-1">Attendance cutoff: 26th to 25th cycle</p>
          </div>
          <ActionButton icon={Plus} label={creating ? 'Generating...' : 'Generate Payroll Draft'} tone="blue" onClick={createDraft} disabled={creating} />
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Draft Payrolls</h3>
            {loading ? (
              <div className="h-32 flex items-center justify-center text-gray-500"><Loader2 className="w-5 h-5 mr-2 animate-spin" />Loading drafts</div>
            ) : (
              <DataList
                items={draftRuns}
                emptyText="No payroll drafts pending."
                renderItem={(run) => (
                  <div key={run.id} className="p-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">{getMonthName(run.payroll_month)} {run.payroll_year}</p>
                      <p className="text-xs text-gray-500">{run.total_employees || 0} employees - {run.run_type}</p>
                    </div>
                    <ActionButton icon={FileText} label="Preview/Edit" tone="gray" onClick={() => setSelectedRunId(run.id)} />
                  </div>
                )}
              />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Recent Approved Payrolls</h3>
            <DataList
              items={approvedRuns.slice(0, 5)}
              emptyText="No approved payrolls available."
              renderItem={(run) => (
                <div key={run.id} className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900">{getMonthName(run.payroll_month)} {run.payroll_year}</p>
                    <p className="text-xs text-gray-500">Net pay {formatCurrency(run.total_net_pay)}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full border text-xs font-medium ${statusClass(run.status)}`}>{run.status}</span>
                </div>
              )}
            />
          </div>
        </div>
      </Section>

      {selectedRunId && (
        <PayrollEditor
          runId={selectedRunId}
          hideApprovalActions
          onClose={() => {
            setSelectedRunId(null);
            loadRuns();
            onRefreshDashboard?.();
          }}
        />
      )}
    </div>
  );
};

const EnhancedMakerDashboard = () => {
  const { user, logout, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const activeTab = searchParams.get('tab') || 'dashboard';

  const setActiveTab = (tabId, replace = false) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!tabId || tabId === 'dashboard') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tabId);
    }
    setSearchParams(nextParams, { replace });
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [kpis, setKpis] = useState(null);
  const [data, setData] = useState({
    attendanceSummary: {},
    attendanceRecords: [],
    absentEmployees: [],
    employees: [],
    payrollDashboard: {},
    payrollRuns: [],
    loans: [],
    advances: [],
    leaves: [],
    loanStats: {},
    shiftSwaps: [],
    overtimeRequests: []
  });

  const isMaker = useMemo(() => {
    const role = String(user?.role_name || user?.role_display_name || '').toLowerCase();
    return (
      role.includes('maker') ||
      role.includes('hr_executive') ||
      role.includes('attendance_operator') ||
      role.includes('payroll_operator') ||
      hasPermission?.('attendance:mark:dept') ||
      hasPermission?.('payroll:process:all') ||
      hasPermission?.('employee.create')
    );
  }, [user, hasPermission]);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const fetchJson = useCallback(async (url, key) => {
    const response = await fetch(buildApiUrl(url), { headers });
    if (!response.ok) throw new Error(await extractError(response, `Unable to load ${key}.`));
    return response.json();
  }, [headers]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    const date = todayIso();

    const requests = await Promise.allSettled([
      fetchJson('/api/v1/workflow/maker/kpis', 'maker KPIs'),
      fetchJson(`/api/v1/attendance/today/all?attendance_date=${date}`, 'attendance'),
      fetchJson('/api/v1/benfish-employees/search?limit=500&offset=0', 'employees'),
      fetchJson('/api/v1/benfish-payroll/runs?limit=50', 'payroll runs'),
      fetchJson('/api/v1/benfish-loans/applications?limit=500', 'loans'),
      fetchJson('/api/v1/benfish-loans/advance-applications?limit=500', 'advances'),
      fetchJson('/api/v1/leaves/applications?status=pending&page=1&size=100', 'leaves'),
    ]);

    const failed = requests.filter((result) => result.status === 'rejected');
    if (failed.length > 0) {
      const message = failed.map((r) => r.reason?.message).filter(Boolean).join(' | ');
      setError(message || 'Some dashboard data could not be loaded.');
    }

    if (requests[0].status === 'fulfilled') setKpis(requests[0].value);

    const attendance = requests[1].status === 'fulfilled' ? requests[1].value : {};
    setData({
      attendanceSummary: attendance.summary || {},
      attendanceRecords: attendance.records || [],
      absentEmployees: [],
      employees: requests[2].status === 'fulfilled' ? getArray(requests[2].value, 'employees') : [],
      payrollDashboard: {},
      payrollRuns: requests[3].status === 'fulfilled' ? normalizePayrollRuns(requests[3].value) : [],
      loans: requests[4].status === 'fulfilled' ? (Array.isArray(requests[4].value) ? requests[4].value : []) : [],
      advances: requests[5].status === 'fulfilled' ? (Array.isArray(requests[5].value) ? requests[5].value : []) : [],
      leaves: requests[6].status === 'fulfilled' ? (Array.isArray(requests[6].value) ? requests[6].value : []) : [],
      loanStats: {},
      shiftSwaps: [],
      overtimeRequests: []
    });
    setLoading(false);
  }, [fetchJson]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const stats = useMemo(() => {
    const draftPayrolls = data.payrollRuns.filter((run) => run.status === 'draft');
    const rejectedPayrolls = data.payrollRuns.filter((run) => run.status === 'rejected');
    const incompleteProfiles = data.employees.filter(
      (employee) => !employee.email || !employee.phone || !employee.department || !employee.designation
    );
    const missingCheckouts = data.attendanceRecords.filter(
      (record) => record.check_in_time && !record.check_out_time
    ).length;
    const pendingAttendanceCorrections = data.attendanceRecords.filter(
      (record) => record.approval_status === 'pending'
    ).length;
    return {
      draftPayrolls,
      rejectedPayrolls,
      incompleteProfiles,
      missingCheckouts,
      pendingAttendanceCorrections,
      kpis: kpis || {},
    };
  }, [data, kpis]);

  const allTabs = [
    { id: 'dashboard', label: 'Operations', icon: Home, requiredPermission: null },
    { id: 'attendance', label: 'Attendance', icon: Clock, requiredPermission: ['attendance:view:all', 'attendance:view:dept', 'attendance:mark:dept'] },
    { id: 'leave', label: 'Leave', icon: Calendar, requiredPermission: ['leave:view:all', 'leave:view:dept', 'leave:create:dept'] },
    { id: 'shifts', label: 'Shift & Roster', icon: Briefcase, requiredPermission: ['shift:view:all', 'shift:view:dept', 'shift:view:own', 'shift:create', 'shift:edit', 'shift:delete', 'shift:assign'] },
    { id: 'employees', label: 'Employees', icon: Users, requiredPermission: ['employee:view:all', 'employee:view:dept', 'employee:create', 'employee:update:all', 'employee:update:dept'] },
    { id: 'payroll', label: 'Payroll Drafts', icon: IndianRupee, requiredPermission: ['payroll:view:all', 'payroll:view:dept', 'payroll:create', 'payroll:update'] },
    //{ id: 'loans', label: 'Loans & Advances', icon: CreditCard, requiredPermission: ['loan:view:all', 'loan:view:dept'] },
    { id: 'reports', label: 'Reports', icon: FileText, requiredPermission: ['report:view:all', 'report:view:dept', 'report:export'] },
  ];

  const tabs = allTabs.filter(tab => {
    if (tab.id === 'dashboard') return true;
    if (Array.isArray(tab.requiredPermission)) {
      return tab.requiredPermission.some(perm => hasPermission(perm));
    }
    return hasPermission(tab.requiredPermission);
  });

  useEffect(() => {
    if (tabs.length > 0 && activeTab !== 'dashboard' && !tabs.some(t => t.id === activeTab)) {
      setActiveTab('dashboard', true);
    }
  }, [tabs, activeTab]);

  const renderDashboard = () => (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center text-yellow-800">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={loadDashboard} className="text-sm font-medium text-yellow-800 hover:text-yellow-900">Retry</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <SummaryCard title="Pending Attendance" value={stats.kpis.pending_attendance ?? 0} icon={Clock} color="bg-yellow-600" actionLabel="Open Attendance" onClick={() => setActiveTab('attendance')} />
        <SummaryCard title="Pending Leave" value={stats.kpis.pending_leave ?? data.leaves.length} icon={Calendar} color="bg-teal-600" actionLabel="Process Leave" onClick={() => setActiveTab('leave')} />
        <SummaryCard title="Pending Employee Actions" value={stats.kpis.pending_employee_actions ?? 0} icon={UserPlus} color="bg-blue-600" actionLabel="Manage Employees" onClick={() => setActiveTab('employees')} />
        <SummaryCard title="Pending Payroll Actions" value={stats.kpis.pending_payroll_actions ?? stats.draftPayrolls.length} icon={IndianRupee} color="bg-purple-600" actionLabel="Payroll Drafts" onClick={() => setActiveTab('payroll')} />
      </div>

      <Section
        title="Attendance Operations"
        subtitle="Manual entry, edit corrections, and daily attendance visibility. Maker does not approve records."
        icon={Clock}
        action={<ActionButton icon={RefreshCw} label="Refresh" onClick={loadDashboard} tone="gray" />}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ActionButton icon={Plus} label="Mark Attendance" onClick={() => setActiveTab('attendance')} tone="blue" />
            <ActionButton icon={FileText} label="View Attendance" onClick={() => setActiveTab('attendance')} tone="green" />
            <ActionButton icon={Briefcase} label="Attendance Reports" onClick={() => setActiveTab('attendance')} tone="purple" />
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm font-medium text-gray-700">Today Overview</p>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Checked In</span><p className="font-semibold">{data.attendanceSummary.checked_in || 0}</p></div>
              <div><span className="text-gray-500">Checked Out</span><p className="font-semibold">{data.attendanceSummary.checked_out || 0}</p></div>
              <div><span className="text-gray-500">Late</span><p className="font-semibold">{data.attendanceSummary.late_employees || 0}</p></div>
              <div><span className="text-gray-500">Total</span><p className="font-semibold">{data.attendanceSummary.total_employees || data.employees.length}</p></div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
          <DataList
            items={data.absentEmployees.slice(0, 5)}
            emptyText="No employees are currently missing check-in."
            renderItem={(employee) => (
              <div key={employee.employee_id} className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{employee.employee_name}</p>
                  <p className="text-xs text-gray-500">{employee.employee_code} - {employee.department || 'No department'}</p>
                </div>
                <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded-full">Not checked in</span>
              </div>
            )}
          />
          <DataList
            items={data.attendanceRecords.filter(record => record.check_in_time && !record.check_out_time).slice(0, 5)}
            emptyText="No missing checkouts found."
            renderItem={(record) => (
              <div key={record.id} className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{record.employee_name}</p>
                  <p className="text-xs text-gray-500">{record.employee_code} - checked in {String(record.check_in_time).slice(0, 5)}</p>
                </div>
                <span className="text-xs font-medium text-orange-700 bg-orange-50 px-2 py-1 rounded-full">Missing checkout</span>
              </div>
            )}
          />
        </div>
      </Section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Section title="Employee Operations" subtitle="Create, edit, onboard, and complete employee records." icon={Users}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <ActionButton icon={UserPlus} label="Add Employee" onClick={() => setActiveTab('employees')} tone="blue" />
            <ActionButton icon={Users} label="Manage Employees" onClick={() => setActiveTab('employees')} tone="green" />
            <ActionButton icon={Upload} label="Upload Documents" onClick={() => setActiveTab('employees')} tone="purple" />
          </div>
          <DataList
            items={stats.incompleteProfiles.slice(0, 5)}
            emptyText="No incomplete profiles detected."
            renderItem={(employee) => (
              <div key={employee.id} className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{employee.full_name}</p>
                  <p className="text-xs text-gray-500">{employee.emp_code} - {employee.department || 'Missing department'}</p>
                </div>
                <span className="text-xs font-medium text-yellow-800 bg-yellow-50 px-2 py-1 rounded-full">Incomplete</span>
              </div>
            )}
          />
        </Section>

        <Section title="Payroll Draft Processing" subtitle="Generate and calculate payroll drafts. Approval and release stay with checker." icon={IndianRupee}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <ActionButton icon={Plus} label="Generate Payroll Draft" onClick={() => setActiveTab('payroll')} tone="blue" />
            <ActionButton icon={IndianRupee} label="View Payroll" onClick={() => setActiveTab('payroll')} tone="green" />
            <ActionButton icon={FileText} label="Payroll Preview" onClick={() => setActiveTab('payroll')} tone="purple" />
          </div>
          <DataList
            items={stats.draftPayrolls.slice(0, 5)}
            emptyText="No payroll drafts pending."
            renderItem={(run) => (
              <div key={run.id} className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{run.payroll_month}/{run.payroll_year}</p>
                  <p className="text-xs text-gray-500">{run.total_employees || 0} employees - {run.run_type}</p>
                </div>
                <span className={`text-xs font-medium border px-2 py-1 rounded-full ${statusClass(run.status)}`}>{run.status}</span>
              </div>
            )}
          />
        </Section>
      </div>

      <Section title="Pending Tasks & Notifications" subtitle="Operational exceptions that need maker cleanup before checker review." icon={Bell}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { label: 'Rejected Payrolls', value: stats.rejectedPayrolls.length, icon: XCircle, tone: 'text-red-700 bg-red-50' },
            { label: 'Pending Attendance', value: stats.kpis.pending_attendance ?? stats.pendingAttendanceCorrections, icon: Edit3, tone: 'text-yellow-800 bg-yellow-50' },
            { label: 'Incomplete Employee Profiles', value: stats.kpis.pending_employee_actions ?? stats.incompleteProfiles.length, icon: AlertCircle, tone: 'text-orange-800 bg-orange-50' },
            { label: 'Draft Loans/Advances', value: (stats.kpis.draft_loans ?? 0) + (stats.kpis.draft_advances ?? 0), icon: CreditCard, tone: 'text-indigo-800 bg-indigo-50' },
          ].map(item => (
            <div key={item.label} className={`p-4 rounded-lg ${item.tone}`}>
              <item.icon className="w-5 h-5 mb-3" />
              <p className="text-2xl font-bold">{item.value}</p>
              <p className="text-sm font-medium">{item.label}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Leave Queue" subtitle="Pending leave applications awaiting maker action." icon={Calendar}>
        <DataList
          items={data.leaves.slice(0, 8)}
          emptyText="No pending leave applications."
          renderItem={(leave) => (
            <div key={leave.id} className="p-3 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{leave.employee_name || 'Employee'}</p>
                <p className="text-xs text-gray-500">{leave.leave_type_name || 'Leave'} — {leave.from_date} to {leave.to_date}</p>
              </div>
              <span className={`text-xs font-medium border px-2 py-1 rounded-full ${statusClass(leave.status)}`}>{leave.status}</span>
            </div>
          )}
        />
      </Section>
    </div>
  );

  const renderContent = () => {
    if (activeTab === 'attendance') return <AttendanceManagement workflowMode="maker" />;
    if (activeTab === 'leave') return <LeaveManagement workflowMode="maker" />;
    if (activeTab === 'shifts') return <ShiftRoster workflowMode="maker" />;
    if (activeTab === 'employees') return <EmployeeManagement />;
    if (activeTab === 'payroll') return <MakerPayrollDraftPanel onRefreshDashboard={loadDashboard} />;
    if (activeTab === 'loans') return <LoansAdvancesManagement workflowMode="maker" />;
    if (activeTab === 'reports') return <Reports reportScope="maker" />;
    if (loading) {
      return (
        <div className="h-64 flex items-center justify-center text-gray-500">
          <Loader2 className="w-6 h-6 mr-2 animate-spin" />
          Loading maker dashboard
        </div>
      );
    }
    return renderDashboard();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center min-w-0">
              <Send className="w-8 h-8 text-blue-600 mr-3 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">Maker Operations Dashboard</h1>
                <p className="text-sm text-gray-600 truncate">Create, correct, process drafts, and prepare records for checker approval</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isMaker && (
                <span className="hidden md:inline-flex px-2 py-1 rounded-full bg-yellow-50 text-yellow-800 text-xs font-medium border border-yellow-200">
                  Limited maker permissions
                </span>
              )}
              <button onClick={loadDashboard} className="p-2 text-gray-400 hover:text-gray-600" title="Refresh dashboard">
                <RefreshCw className="w-5 h-5" />
              </button>
              {/* <button className="p-2 text-gray-400 hover:text-gray-600">
                <Settings className="w-5 h-5" />
              </button> */}
              <div className="relative">
                <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-2 p-2 text-gray-700 hover:text-gray-900">
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">{user?.employee_name || 'Maker'}</span>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.employee_name || user?.username}</p>
                      <p className="text-xs text-gray-500">{user?.role_display_name || user?.role_name}</p>
                    </div>
                    <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-50">
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-gray-200 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6 min-w-max">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 border-b-2 text-sm font-medium ${
                  activeTab === tab.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {renderContent()}
      </main>
    </div>
  );
};

export default EnhancedMakerDashboard;
